import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CheckCircle2Icon,
  XCircleIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  MailIcon,
} from 'lucide-react';
import ApiClient from '@/shared/services/api';
import { Wordmark } from '@/shared/components/Wordmark';
import { Spinner } from '@/shared/components/Spinner';
import { ThemeToggle } from '@/shared/components/ThemeToggle';
import { newPasswordSchema, type NewPasswordFormData } from '@/features/auth/lib/passwordSchema';

type Status = 'form' | 'success' | 'invalid';

export const ConfirmEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId') ?? '';
  const token = searchParams.get('token') ?? '';

  const hasLink = Boolean(userId && token);
  const [status, setStatus] = useState<Status>(hasLink ? 'form' : 'invalid');
  const [message, setMessage] = useState<string>(
    hasLink ? '' : 'This confirmation link is invalid or incomplete.'
  );
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);

  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
  });

  const onSubmit = async (data: NewPasswordFormData) => {
    setServerErrors([]);
    setMessage('');
    try {
      const result = await ApiClient.confirmEmail({
        UserId: userId,
        Token: token,
        Password: data.password,
      });

      if (result.success) {
        setStatus('success');
        setMessage(result.message || 'Your account has been activated.');
        return;
      }

      // Password-policy failures come back as a list; keep the form so the user can retry.
      // A bare message with no field errors means the link itself is invalid/expired.
      if (result.errors && result.errors.length > 0) {
        setServerErrors(result.errors);
        setMessage(result.message || 'The password does not meet the requirements.');
      } else {
        setStatus('invalid');
        setMessage(result.message || 'This confirmation link is invalid or has expired.');
      }
    } catch (error) {
      const data = (error as { response?: { data?: { message?: string; errors?: string[] } } })
        .response?.data;
      if (data?.errors && data.errors.length > 0) {
        setServerErrors(data.errors);
        setMessage(data.message || 'The password does not meet the requirements.');
      } else {
        setStatus('invalid');
        setMessage(data?.message || 'This confirmation link is invalid or has expired.');
      }
    }
  };

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) return;

    setResending(true);
    try {
      // The link carries userId, not the email, so we collect it here. The endpoint always
      // succeeds (no account enumeration), so we always show the same confirmation.
      await ApiClient.resendConfirmation({ Email: resendEmail });
      setResent(true);
    } catch {
      setResent(true);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-12 sm:px-6 lg:px-8">
      {/* Brand-tinted ambient glow (subtle in both themes) */}
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />

      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="relative w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center">
          <Wordmark className="scale-110" />
          <h1 className="mt-6 text-2xl font-bold tracking-tight text-foreground">
            Activate your account
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Feed aggregation, classification &amp; task tracking
          </p>
        </div>

        <div className="card mt-8 p-8 shadow-modal">
          {status === 'form' && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <p className="text-sm text-muted-foreground">
                Confirm your email by choosing a password for your account.
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="password" className="label">
                    Password
                  </label>
                  <div className="relative mt-1.5">
                    <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      {...register('password')}
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      className="input pl-9 pr-10"
                      placeholder="New password"
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground transition-colors hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOffIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-danger">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">
                    Confirm password
                  </label>
                  <div className="relative mt-1.5">
                    <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      {...register('confirmPassword')}
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      className="input pl-9"
                      placeholder="Confirm password"
                    />
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-danger">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              {(message || serverErrors.length > 0) && (
                <div className="rounded-md border border-danger/30 bg-danger-subtle p-4">
                  {message && <p className="text-sm text-danger-foreground">{message}</p>}
                  {serverErrors.length > 0 && (
                    <ul className="mt-1 list-disc pl-5 text-sm text-danger-foreground">
                      {serverErrors.map((err) => (
                        <li key={err}>{err}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
                {isSubmitting && <Spinner className="text-primary-foreground" />}
                {isSubmitting ? 'Activating…' : 'Activate account'}
              </button>
            </form>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle">
                <CheckCircle2Icon className="h-7 w-7 text-success" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-foreground">
                Account activated
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">{message}</p>
              <Link to="/login" className="btn-primary mt-6 w-full">
                Continue to sign in
              </Link>
            </div>
          )}

          {status === 'invalid' && (
            <div className="flex flex-col items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger-subtle">
                <XCircleIcon className="h-7 w-7 text-danger" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-foreground">
                Activation failed
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">{message}</p>

              {resent ? (
                <div className="mt-6 flex items-center gap-2 rounded-md border border-success/30 bg-success-subtle p-3 text-sm text-success-foreground">
                  <MailIcon className="h-4 w-4 shrink-0" />
                  If the account exists and is not yet confirmed, a new activation email has been sent.
                </div>
              ) : (
                <form onSubmit={handleResend} className="mt-6 w-full space-y-3">
                  <div className="relative">
                    <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      required
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="input pl-9"
                      placeholder="Enter your email to resend"
                      autoComplete="email"
                    />
                  </div>
                  <button type="submit" disabled={resending} className="btn-secondary w-full">
                    {resending && <Spinner />}
                    {resending ? 'Sending…' : 'Resend activation email'}
                  </button>
                </form>
              )}

              <Link
                to="/login"
                className="mt-4 text-sm font-medium text-primary hover:text-primary/80"
              >
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
